import { useSeoMeta } from '@unhead/react';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { cn } from '@/lib/utils';

// ── Ollama config type ─────────────────────────────────────────────────────────
interface OllamaConfig {
  url: string;
  model: string;
}

const DEFAULT_OLLAMA: OllamaConfig = {
  url: 'http://localhost:11434',
  model: 'gemma4:e4b',
};

// ── WAV builder (16-bit PCM, mono, 16 kHz) ───────────────────────────────────
// Ollama audio models require a proper RIFF/WAVE header at exactly 16 kHz mono.
function encodeWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  // Resample to 16 kHz via simple linear interpolation if needed
  const targetRate = 16000;
  let pcm: Float32Array;
  if (sampleRate === targetRate) {
    pcm = samples;
  } else {
    const ratio = sampleRate / targetRate;
    const outLen = Math.floor(samples.length / ratio);
    pcm = new Float32Array(outLen);
    for (let i = 0; i < outLen; i++) {
      const src = i * ratio;
      const lo = Math.floor(src);
      const hi = Math.min(lo + 1, samples.length - 1);
      const t = src - lo;
      pcm[i] = samples[lo] * (1 - t) + samples[hi] * t;
    }
  }

  // Convert float32 → int16
  const int16 = new Int16Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) {
    const s = Math.max(-1, Math.min(1, pcm[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }

  const dataLen = int16.length * 2;
  const buf = new ArrayBuffer(44 + dataLen);
  const view = new DataView(buf);
  const writeStr = (off: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(off + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataLen, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);       // chunk size
  view.setUint16(20, 1, true);        // PCM
  view.setUint16(22, 1, true);        // mono
  view.setUint32(24, targetRate, true);
  view.setUint32(28, targetRate * 2, true); // byte rate
  view.setUint16(32, 2, true);        // block align
  view.setUint16(34, 16, true);       // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataLen, true);
  const u8 = new Uint8Array(buf, 44);
  for (let i = 0; i < int16.length; i++) {
    u8[i * 2]     = int16[i] & 0xff;
    u8[i * 2 + 1] = (int16[i] >> 8) & 0xff;
  }
  return buf;
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface KeywordMatch {
  id: string;
  keyword: string;
  context: string;
  timestamp: Date;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition: new () => SpeechRecognitionInstance;
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTimestamp(d: Date): string {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDateLabel(d: Date): string {
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Waveform Canvas ───────────────────────────────────────────────────────────

function WaveformDisplay({ analyser, isListening }: { analyser: AnalyserNode | null; isListening: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const draw = () => {
      animRef.current = requestAnimationFrame(draw);
      const W = canvas.width;
      const H = canvas.height;

      ctx.fillStyle = '#0a0a1a';
      ctx.fillRect(0, 0, W, H);

      // Grid lines
      ctx.strokeStyle = 'rgba(99,102,241,0.12)';
      ctx.lineWidth = 1;
      for (let i = 0; i <= 4; i++) {
        const y = (H / 4) * i;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }

      if (!analyser || !isListening) {
        // Idle flat line with subtle pulse
        const t = Date.now() / 1000;
        ctx.strokeStyle = 'rgba(99,102,241,0.35)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let x = 0; x < W; x++) {
          const y = H / 2 + Math.sin((x / W) * Math.PI * 6 + t * 0.8) * 2;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
        return;
      }

      const bufLen = analyser.frequencyBinCount;
      const dataArr = new Uint8Array(bufLen);
      analyser.getByteTimeDomainData(dataArr);

      // Gradient stroke
      const grad = ctx.createLinearGradient(0, 0, W, 0);
      grad.addColorStop(0, '#6366f1');
      grad.addColorStop(0.5, '#a78bfa');
      grad.addColorStop(1, '#38bdf8');

      ctx.strokeStyle = grad;
      ctx.lineWidth = 2.5;
      ctx.shadowBlur = 8;
      ctx.shadowColor = '#818cf8';
      ctx.beginPath();

      const sliceW = W / bufLen;
      let x = 0;
      for (let i = 0; i < bufLen; i++) {
        const v = dataArr[i] / 128.0;
        const y = (v * H) / 2;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        x += sliceW;
      }
      ctx.lineTo(W, H / 2);
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Frequency bars at bottom
      const freqData = new Uint8Array(bufLen);
      analyser.getByteFrequencyData(freqData);
      const barW = (W / bufLen) * 2.5;
      let bx = 0;
      for (let i = 0; i < bufLen; i++) {
        const bh = (freqData[i] / 255) * (H * 0.3);
        const alpha = freqData[i] / 255;
        ctx.fillStyle = `rgba(99,102,241,${alpha * 0.6})`;
        ctx.fillRect(bx, H - bh, barW - 1, bh);
        bx += barW + 1;
      }
    };

    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [analyser, isListening]);

  // Resize observer
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    });
    ro.observe(canvas);
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    return () => ro.disconnect();
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full rounded-lg"
      style={{ display: 'block' }}
    />
  );
}

// ── Mobile Drawer ─────────────────────────────────────────────────────────────

function MobileDrawer({
  title,
  children,
  isOpen,
  onClose,
  side = 'left',
}: {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  side?: 'left' | 'right';
}) {
  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      <div
        className={cn(
          'fixed top-0 bottom-0 z-50 w-[85vw] max-w-sm bg-[#0f0f23] border-border flex flex-col transition-transform duration-300 ease-in-out md:hidden',
          side === 'left' ? 'left-0 border-r' : 'right-0 border-l',
          isOpen
            ? 'translate-x-0'
            : side === 'left'
            ? '-translate-x-full'
            : 'translate-x-full'
        )}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-semibold text-sm text-foreground">{title}</span>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </>
  );
}

// ── Keywords Panel ────────────────────────────────────────────────────────────

function KeywordsPanel({
  keywords,
  onAdd,
  onDelete,
}: {
  keywords: string[];
  onAdd: (kw: string) => void;
  onDelete: (kw: string) => void;
}) {
  const [input, setInput] = useState('');

  const handleAdd = () => {
    const trimmed = input.trim().toLowerCase();
    if (trimmed && !keywords.includes(trimmed)) {
      onAdd(trimmed);
      setInput('');
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleAdd();
  };

  return (
    <div className="flex flex-col h-full p-4 gap-4">
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3">
          Keywords
        </h2>
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Add keyword…"
            className="flex-1 min-w-0 bg-[#1a1a2e] border border-indigo-900/60 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-indigo-500 transition"
          />
          <button
            onClick={handleAdd}
            disabled={!input.trim()}
            className="shrink-0 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-lg px-3 py-2 text-sm font-medium transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-1.5">
        {keywords.length === 0 ? (
          <div className="border border-dashed border-indigo-900/40 rounded-lg py-8 px-4 text-center">
            <p className="text-xs text-muted-foreground">No keywords yet. Add one above.</p>
          </div>
        ) : (
          keywords.map((kw) => (
            <div
              key={kw}
              className="group flex items-center justify-between gap-2 bg-[#1a1a2e] hover:bg-[#1e1e35] border border-indigo-900/30 rounded-lg px-3 py-2 transition-colors"
            >
              <span className="text-sm text-indigo-200 font-mono truncate">{kw}</span>
              <button
                onClick={() => onDelete(kw)}
                className="shrink-0 text-muted-foreground hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                aria-label={`Delete keyword "${kw}"`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>

      <div className="pt-2 border-t border-border text-xs text-muted-foreground text-center">
        {keywords.length} keyword{keywords.length !== 1 ? 's' : ''} monitored
      </div>
    </div>
  );
}

// ── Matches Panel ─────────────────────────────────────────────────────────────

function MatchesPanel({
  matches,
  onClear,
}: {
  matches: KeywordMatch[];
  onClear: () => void;
}) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [matches]);

  return (
    <div className="flex flex-col h-full p-4 gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-emerald-400">
          Intercepts
        </h2>
        {matches.length > 0 && (
          <button
            onClick={onClear}
            className="text-xs text-muted-foreground hover:text-red-400 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto space-y-2">
        {matches.length === 0 ? (
          <div className="border border-dashed border-emerald-900/40 rounded-lg py-8 px-4 text-center">
            <p className="text-xs text-muted-foreground">Keyword matches will appear here.</p>
          </div>
        ) : (
          matches.map((m) => (
            <div
              key={m.id}
              className="bg-[#0d1f1a] border border-emerald-900/40 rounded-lg px-3 py-2.5 space-y-1"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold font-mono text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded">
                  {m.keyword}
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatTimestamp(m.timestamp)}
                </span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed line-clamp-3">
                {highlightKeyword(m.context, m.keyword)}
              </p>
              <p className="text-[10px] text-muted-foreground">{formatDateLabel(m.timestamp)}</p>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      <div className="pt-2 border-t border-border text-xs text-muted-foreground text-center">
        {matches.length} intercept{matches.length !== 1 ? 's' : ''} logged
      </div>
    </div>
  );
}

function highlightKeyword(text: string, keyword: string) {
  const regex = new RegExp(`(${keyword})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === keyword.toLowerCase() ? (
          <mark key={i} className="bg-emerald-600/40 text-emerald-200 rounded px-0.5 not-italic">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

// ── Ollama Settings Panel ─────────────────────────────────────────────────────

function OllamaSettingsPanel({
  config,
  onChange,
}: {
  config: OllamaConfig;
  onChange: (c: OllamaConfig) => void;
}) {
  const [url, setUrl] = useState(config.url);
  const [model, setModel] = useState(config.model);
  const [models, setModels] = useState<string[]>([]);
  const [status, setStatus] = useState<'idle' | 'testing' | 'ok' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');

  const testConnection = async () => {
    setStatus('testing');
    setStatusMsg('');
    try {
      const res = await fetch(`${url.replace(/\/$/, '')}/api/tags`, { signal: AbortSignal.timeout(5000) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { models?: { name: string }[] };
      const names = (data.models ?? []).map((m) => m.name);
      setModels(names);
      setStatus('ok');
      setStatusMsg(`Connected — ${names.length} model${names.length !== 1 ? 's' : ''} available`);
    } catch (e) {
      setStatus('error');
      setStatusMsg(e instanceof Error ? e.message : 'Connection failed');
      setModels([]);
    }
  };

  const save = () => {
    onChange({ url: url.replace(/\/$/, ''), model });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-violet-400">
        Ollama Server
      </h2>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs text-muted-foreground">Server URL</label>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="http://localhost:11434"
          className="bg-[#1a1a2e] border border-indigo-900/60 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-violet-500 transition"
        />
      </div>

      <button
        onClick={testConnection}
        disabled={status === 'testing'}
        className="flex items-center justify-center gap-2 bg-violet-700 hover:bg-violet-600 disabled:opacity-50 text-white rounded-lg px-3 py-2 text-sm font-medium transition-colors"
      >
        {status === 'testing' ? (
          <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        )}
        Test Connection
      </button>

      {statusMsg && (
        <p className={cn('text-xs rounded-lg px-3 py-2', status === 'ok' ? 'text-emerald-300 bg-emerald-950/50 border border-emerald-800/40' : 'text-red-300 bg-red-950/50 border border-red-800/40')}>
          {statusMsg}
        </p>
      )}

      <div className="flex flex-col gap-1.5">
        <label className="text-xs text-muted-foreground">Model</label>
        {models.length > 0 ? (
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="bg-[#1a1a2e] border border-indigo-900/60 rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-violet-500 transition"
          >
            {models.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="gemma4:e4b"
            className="bg-[#1a1a2e] border border-indigo-900/60 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-violet-500 transition"
          />
        )}
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          Requires an audio-capable model, e.g. <span className="font-mono text-violet-400">gemma4:e4b</span>
        </p>
      </div>

      <button
        onClick={save}
        className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg px-3 py-2 text-sm font-semibold transition-colors"
      >
        Save Settings
      </button>

      <div className="border-t border-border pt-3 text-[10px] text-muted-foreground space-y-1 leading-relaxed">
        <p className="font-semibold text-slate-400">How it works</p>
        <p>Audio is captured in 5-second chunks, resampled to 16 kHz mono WAV, and sent to <span className="font-mono">/api/chat</span> with a transcribe prompt.</p>
        <p>Make sure Ollama is running with <span className="font-mono text-violet-400">OLLAMA_ORIGINS=*</span> to allow browser requests.</p>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const Index = () => {
  useSeoMeta({
    title: 'Eshu — Keyword Monitor',
    description: 'Real-time speech-to-text keyword monitoring with waveform display.',
  });

  // ── State ──────────────────────────────────────────────────────────────────
  const [keywords, setKeywords] = useLocalStorage<string[]>('eshu:keywords', []);
  const [matches, setMatches] = useLocalStorage<KeywordMatch[]>('eshu:matches', []);
  const [transcript, setTranscript] = useState('');
  const [interimText, setInterimText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);

  // Ollama
  type Engine = 'browser' | 'ollama';
  const [engine, setEngine] = useLocalStorage<Engine>('eshu:engine', 'browser');
  const [ollamaConfig, setOllamaConfig] = useLocalStorage<OllamaConfig>('eshu:ollama', DEFAULT_OLLAMA);
  const [showOllamaSettings, setShowOllamaSettings] = useState(false);

  // Ollama recorder refs
  const ollamaActiveRef = useRef(false);
  const ollamaLoopRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const transcriptRef = useRef('');
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);

  // Keep ref in sync for use inside callbacks
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  // ── Keyword helpers ────────────────────────────────────────────────────────
  const addKeyword = useCallback(
    (kw: string) => setKeywords((prev) => [...prev, kw]),
    [setKeywords]
  );

  const deleteKeyword = useCallback(
    (kw: string) => setKeywords((prev) => prev.filter((k) => k !== kw)),
    [setKeywords]
  );

  const clearMatches = useCallback(() => setMatches([]), [setMatches]);

  // ── Scan for keyword hits ──────────────────────────────────────────────────
  const scanForKeywords = useCallback(
    (text: string, kws: string[]) => {
      const lower = text.toLowerCase();
      const hits: KeywordMatch[] = [];
      for (const kw of kws) {
        if (lower.includes(kw)) {
          // Extract surrounding context (±40 chars)
          const idx = lower.indexOf(kw);
          const start = Math.max(0, idx - 40);
          const end = Math.min(text.length, idx + kw.length + 40);
          const context = (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '');
          hits.push({
            id: `${Date.now()}-${kw}-${Math.random()}`,
            keyword: kw,
            context,
            timestamp: new Date(),
          });
        }
      }
      if (hits.length > 0) {
        setMatches((prev) => [...prev, ...hits]);
      }
    },
    [setMatches]
  );

  // ── Audio context + analyser ───────────────────────────────────────────────
  const setupAudio = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const node = ctx.createAnalyser();
      node.fftSize = 1024;
      node.smoothingTimeConstant = 0.75;
      source.connect(node);
      setAnalyser(node);
    } catch {
      // Visualisation unavailable — speech recognition can still work
    }
  }, []);

  const teardownAudio = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    audioCtxRef.current?.close();
    audioCtxRef.current = null;
    setAnalyser(null);
  }, []);

  // ── Ollama transcription loop ──────────────────────────────────────────────
  // Records CHUNK_SEC seconds of PCM audio, encodes as WAV 16 kHz mono,
  // base64-encodes it, and sends to /api/chat with a transcribe prompt.
  const CHUNK_SEC = 5;

  const startOllama = useCallback(async () => {
    setError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError('Microphone access denied. Please allow mic permission and try again.');
      return;
    }

    // Set up waveform analyser using the same stream
    streamRef.current = stream;
    const actx = new AudioContext();
    audioCtxRef.current = actx;
    const src = actx.createMediaStreamSource(stream);
    const analyserNode = actx.createAnalyser();
    analyserNode.fftSize = 1024;
    analyserNode.smoothingTimeConstant = 0.75;
    src.connect(analyserNode);
    setAnalyser(analyserNode);

    ollamaActiveRef.current = true;
    setIsListening(true);

    const sampleRate = actx.sampleRate;

    const runChunk = async () => {
      if (!ollamaActiveRef.current) return;

      // Collect CHUNK_SEC seconds of raw PCM via ScriptProcessorNode
      const bufferSize = 4096;
      const collected: Float32Array[] = [];
      let totalSamples = 0;
      const targetSamples = sampleRate * CHUNK_SEC;

      await new Promise<void>((resolve) => {
        // Use ScriptProcessorNode (widely supported, no extra deps)
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        const processor = actx.createScriptProcessor(bufferSize, 1, 1);
        src.connect(processor);
        processor.connect(actx.destination);

        processor.onaudioprocess = (ev) => {
          if (!ollamaActiveRef.current) {
            processor.disconnect();
            resolve();
            return;
          }
          const channelData = ev.inputBuffer.getChannelData(0);
          collected.push(new Float32Array(channelData));
          totalSamples += channelData.length;
          if (totalSamples >= targetSamples) {
            processor.disconnect();
            resolve();
          }
        };
      });

      if (!ollamaActiveRef.current) return;

      // Merge chunks
      const merged = new Float32Array(totalSamples);
      let offset = 0;
      for (const chunk of collected) {
        merged.set(chunk, offset);
        offset += chunk.length;
      }

      // Encode WAV and base64
      const wavBuf = encodeWav(merged, sampleRate);
      const b64 = arrayBufferToBase64(wavBuf);

      // Send to Ollama /api/chat
      try {
        setInterimText('Transcribing…');
        const res = await fetch(`${ollamaConfig.url}/api/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            model: ollamaConfig.model,
            stream: false,
            messages: [{
              role: 'user',
              content: 'transcribe',
              images: [b64],
            }],
            options: { num_ctx: 8192 },
          }),
          signal: AbortSignal.timeout(30000),
        });

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`Ollama error ${res.status}: ${txt.slice(0, 120)}`);
        }

        const data = await res.json() as { message?: { content?: string } };
        const text = data.message?.content?.trim() ?? '';

        setInterimText('');

        if (text && text.length > 1) {
          setTranscript((prev) => prev + text + ' ');
          setKeywords((kws) => {
            scanForKeywords(text, kws);
            return kws;
          });
        }
      } catch (e) {
        setInterimText('');
        if (ollamaActiveRef.current) {
          setError(e instanceof Error ? e.message : 'Ollama request failed');
        }
      }

      // Schedule next chunk
      if (ollamaActiveRef.current) {
        ollamaLoopRef.current = setTimeout(runChunk, 100);
      }
    };

    runChunk();
  }, [ollamaConfig, scanForKeywords, setKeywords]);

  const stopOllama = useCallback(() => {
    ollamaActiveRef.current = false;
    if (ollamaLoopRef.current) {
      clearTimeout(ollamaLoopRef.current);
      ollamaLoopRef.current = null;
    }
    teardownAudio();
    setIsListening(false);
    setInterimText('');
  }, [teardownAudio]);

  // ── Speech recognition ─────────────────────────────────────────────────────
  const startListening = useCallback(async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setError('Speech recognition is not supported in this browser. Try Chrome or Edge.');
      return;
    }
    setError(null);
    await setupAudio();

    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    recognitionRef.current = rec;

    rec.onstart = () => {
      setIsListening(true);
      retryCountRef.current = 0; // reset backoff on each successful start
    };

    rec.onresult = (e: SpeechRecognitionEvent) => {
      let interim = '';
      let finalChunk = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i];
        if (result.isFinal) {
          finalChunk += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      if (finalChunk) {
        const newTranscript = transcriptRef.current + finalChunk + ' ';
        setTranscript(newTranscript);
        // Use fresh keywords from localStorage ref via a closure trick via scanForKeywords
        setKeywords((kws) => {
          scanForKeywords(finalChunk, kws);
          return kws; // no-op update
        });
      }
      setInterimText(interim);
    };

    // Errors that are transient — silently retry
    const transientErrors = new Set(['network', 'no-speech', 'audio-capture', 'service-not-allowed']);

    rec.onerror = (e: SpeechRecognitionErrorEvent) => {
      if (transientErrors.has(e.error)) return; // handled by onend / retry logic
      // Fatal errors worth surfacing (e.g. 'not-allowed' = mic denied)
      setError(
        e.error === 'not-allowed'
          ? 'Microphone access denied. Please allow mic permission and try again.'
          : `Recognition error: ${e.error}`
      );
    };

    rec.onend = () => {
      // Don't restart if the user explicitly stopped
      if (!recognitionRef.current) return;

      // Exponential backoff: 250ms → 500ms → 1000ms → cap 3000ms
      const delay = Math.min(250 * Math.pow(2, retryCountRef.current), 3000);
      retryCountRef.current += 1;

      retryTimerRef.current = setTimeout(() => {
        if (!recognitionRef.current) return;
        try {
          recognitionRef.current.start();
        } catch {
          /* already starting */
        }
      }, delay);
    };

    rec.start();
  }, [setupAudio, scanForKeywords, setKeywords]);

  const stopListening = useCallback(() => {
    // Cancel any pending retry
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    retryCountRef.current = 0;
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    teardownAudio();
    setIsListening(false);
    setInterimText('');
  }, [teardownAudio]);

  const toggleListening = () => {
    if (isListening) {
      if (engine === 'ollama') stopOllama(); else stopListening();
    } else {
      if (engine === 'ollama') startOllama(); else startListening();
    }
  };

  const clearTranscript = () => {
    setTranscript('');
    setInterimText('');
  };

  // Cleanup on unmount
  useEffect(() => () => { stopListening(); stopOllama(); }, [stopListening, stopOllama]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen bg-[#070714] text-foreground overflow-hidden select-none">

      {/* ── Header ── */}
      <header className="shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-indigo-900/30 bg-[#0a0a1a]/90 backdrop-blur-sm z-10">
        {/* Mobile: left drawer toggle */}
        <button
          onClick={() => setLeftOpen(true)}
          className="md:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Open keywords"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-900/50">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </div>
          <h1 className="text-lg font-bold tracking-tight bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">
            Eshu
          </h1>
          <span className="hidden sm:inline text-[10px] text-muted-foreground font-mono uppercase tracking-widest ml-1">
            keyword monitor
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Engine badge */}
          <div className={cn(
            'hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium border transition-all',
            isListening
              ? 'text-emerald-300 border-emerald-700/50 bg-emerald-950/50'
              : 'text-slate-400 border-slate-700/50 bg-slate-900/50'
          )}>
            <span className={cn(
              'w-1.5 h-1.5 rounded-full',
              isListening ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'
            )} />
            {isListening ? 'Listening' : 'Idle'}
            <span className={cn('ml-0.5 font-mono text-[10px] opacity-70', engine === 'ollama' ? 'text-violet-300' : 'text-indigo-300')}>
              [{engine}]
            </span>
          </div>

          {/* Mobile: right drawer toggle */}
          <button
            onClick={() => setRightOpen(true)}
            className="md:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors relative"
            aria-label="Open intercepts"
          >
            {matches.length > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-emerald-400 rounded-full" />
            )}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          </button>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left Column (desktop) ── */}
        <aside className="hidden md:flex flex-col w-56 xl:w-64 shrink-0 border-r border-indigo-900/30 bg-[#0a0a1a] overflow-hidden">
          <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
            <KeywordsPanel keywords={keywords} onAdd={addKeyword} onDelete={deleteKeyword} />
          </div>
          {/* Engine + Ollama settings */}
          <div className="shrink-0 border-t border-indigo-900/30">
            <button
              onClick={() => setShowOllamaSettings((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#12122a] transition-colors group"
            >
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-violet-400">Ollama</span>
                <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-mono', engine === 'ollama' ? 'bg-violet-900/60 text-violet-300' : 'bg-slate-800 text-slate-500')}>
                  {engine === 'ollama' ? 'active' : 'off'}
                </span>
              </div>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={cn('text-muted-foreground transition-transform', showOllamaSettings ? 'rotate-180' : '')}>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {showOllamaSettings && (
              <div className="border-t border-indigo-900/30 overflow-y-auto max-h-96">
                {/* Engine toggle */}
                <div className="px-4 pt-3 pb-2 flex gap-2">
                  <button
                    onClick={() => { if (isListening) { stopOllama(); stopListening(); } setEngine('browser'); }}
                    className={cn('flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors', engine === 'browser' ? 'bg-indigo-600 text-white' : 'bg-[#1a1a2e] text-muted-foreground hover:text-foreground')}
                  >
                    Browser
                  </button>
                  <button
                    onClick={() => { if (isListening) { stopOllama(); stopListening(); } setEngine('ollama'); }}
                    className={cn('flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors', engine === 'ollama' ? 'bg-violet-600 text-white' : 'bg-[#1a1a2e] text-muted-foreground hover:text-foreground')}
                  >
                    Ollama
                  </button>
                </div>
                <OllamaSettingsPanel config={ollamaConfig} onChange={setOllamaConfig} />
              </div>
            )}
          </div>
        </aside>

        {/* ── Center Column ── */}
        <main className="flex flex-col flex-1 min-w-0 overflow-hidden p-3 gap-3">

          {/* Error banner */}
          {error && (
            <div className="shrink-0 bg-red-950/70 border border-red-700/50 rounded-lg px-4 py-2.5 text-sm text-red-300 flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}

          {/* Waveform */}
          <div className="shrink-0 h-36 sm:h-44 rounded-xl border border-indigo-900/40 bg-[#0a0a1a] overflow-hidden relative">
            <WaveformDisplay analyser={analyser} isListening={isListening} />
            {/* Listen button overlay */}
            <div className="absolute bottom-3 right-3">
              <button
                onClick={toggleListening}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shadow-lg transition-all',
                  isListening
                    ? 'bg-red-600 hover:bg-red-500 text-white shadow-red-900/40'
                    : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-900/40'
                )}
              >
                {isListening ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="6" width="12" height="12" rx="1" />
                    </svg>
                    Stop
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    </svg>
                    Listen
                  </>
                )}
              </button>
            </div>
            {/* Live indicator */}
            {isListening && (
              <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/60 rounded-full px-2.5 py-1 backdrop-blur-sm">
                <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse" />
                <span className="text-[10px] font-semibold text-red-300 uppercase tracking-widest">Live</span>
                <span className={cn('text-[10px] font-mono', engine === 'ollama' ? 'text-violet-400' : 'text-indigo-400')}>
                  {engine}
                </span>
              </div>
            )}
          </div>

          {/* Transcript */}
          <div className="flex flex-col flex-1 min-h-0 rounded-xl border border-indigo-900/40 bg-[#0a0a1a] overflow-hidden">
            <div className="shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-indigo-900/30">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-indigo-400">
                Transcript
              </h2>
              <button
                onClick={clearTranscript}
                disabled={!transcript && !interimText}
                className="text-xs text-muted-foreground hover:text-red-400 disabled:opacity-30 transition-colors"
              >
                Clear
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {!transcript && !interimText ? (
                <p className="text-sm text-muted-foreground italic">
                  {isListening
                    ? 'Listening… start speaking.'
                    : 'Press Listen to begin transcription.'}
                </p>
              ) : (
                <p className="text-sm leading-relaxed text-slate-200 whitespace-pre-wrap">
                  {transcript && <TranscriptWithHighlights text={transcript} keywords={keywords} />}
                  {interimText && (
                    <span className="text-muted-foreground italic">{interimText}</span>
                  )}
                </p>
              )}
            </div>
          </div>


        </main>

        {/* ── Right Column (desktop) ── */}
        <aside className="hidden md:flex flex-col w-64 xl:w-72 shrink-0 border-l border-indigo-900/30 bg-[#0a0a1a] overflow-hidden">
          <MatchesPanel matches={matches} onClear={clearMatches} />
        </aside>
      </div>

      {/* ── Mobile Drawers ── */}
      <MobileDrawer title="Keywords" isOpen={leftOpen} onClose={() => setLeftOpen(false)} side="left">
        <KeywordsPanel keywords={keywords} onAdd={addKeyword} onDelete={deleteKeyword} />
        <div className="border-t border-indigo-900/30">
          <button
            onClick={() => setShowOllamaSettings((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#12122a] transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-violet-400">Ollama</span>
              <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-mono', engine === 'ollama' ? 'bg-violet-900/60 text-violet-300' : 'bg-slate-800 text-slate-500')}>
                {engine === 'ollama' ? 'active' : 'off'}
              </span>
            </div>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={cn('text-muted-foreground transition-transform', showOllamaSettings ? 'rotate-180' : '')}>
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {showOllamaSettings && (
            <div className="border-t border-indigo-900/30">
              <div className="px-4 pt-3 pb-2 flex gap-2">
                <button
                  onClick={() => { if (isListening) { stopOllama(); stopListening(); } setEngine('browser'); }}
                  className={cn('flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors', engine === 'browser' ? 'bg-indigo-600 text-white' : 'bg-[#1a1a2e] text-muted-foreground hover:text-foreground')}
                >
                  Browser
                </button>
                <button
                  onClick={() => { if (isListening) { stopOllama(); stopListening(); } setEngine('ollama'); }}
                  className={cn('flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors', engine === 'ollama' ? 'bg-violet-600 text-white' : 'bg-[#1a1a2e] text-muted-foreground hover:text-foreground')}
                >
                  Ollama
                </button>
              </div>
              <OllamaSettingsPanel config={ollamaConfig} onChange={setOllamaConfig} />
            </div>
          )}
        </div>
      </MobileDrawer>

      <MobileDrawer title="Intercepts" isOpen={rightOpen} onClose={() => setRightOpen(false)} side="right">
        <MatchesPanel matches={matches} onClear={clearMatches} />
      </MobileDrawer>
    </div>
  );
};

// ── Transcript with keyword highlights ────────────────────────────────────────

function TranscriptWithHighlights({ text, keywords }: { text: string; keywords: string[] }) {
  if (keywords.length === 0) return <>{text}</>;

  const pattern = keywords.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const regex = new RegExp(`(${pattern})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) =>
        keywords.some((k) => k.toLowerCase() === part.toLowerCase()) ? (
          <mark key={i} className="bg-indigo-600/40 text-indigo-200 rounded px-0.5 not-italic">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export default Index;
